import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  ArrowLeft,
  Check, 
  X, 
  AlertCircle,
  ExternalLink,
  Calendar,
  Share2,
  Loader2,
  AlertTriangle,
  Lightbulb,
  Eye,
  Sparkles,
  ArrowRight,
  ShoppingBag,
  HelpCircle,
  Target,
  Flag
} from 'lucide-react';
import { DonutChart, SmallDonut, ScoreSummaryBar } from '../components/charts/DonutChart';
import PacerLogo from '../components/PacerLogo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HUBSPOT_LINK = "https://meetings-eu1.hubspot.com/joe-egan";
const MIGRATION_LINK = "https://www.shopify.com/plus/commerce-components";

// Methodology tooltips for each check type
const CHECK_METHODOLOGY = {
  'UCP Profile': { lookFor: '/.well-known/ucp endpoint', where: '{domain}/.well-known/ucp', pass: 'HTTP 200 + valid JSON with capabilities' },
  'Checkout Capability': { lookFor: '"checkout" in UCP capabilities', where: 'UCP profile JSON', pass: 'Capability declared in profile' },
  'Identity Linking': { lookFor: '"identity", "oauth", or "auth" in capabilities', where: 'UCP profile JSON', pass: 'Capability declared' },
  'Order Management': { lookFor: '"order" or "tracking" in capabilities', where: 'UCP profile JSON', pass: 'Capability declared' },
  'llms.txt': { lookFor: 'LLM instructions file', where: '{domain}/llms.txt', pass: 'HTTP 200 + content > 100 chars' },
  'Cart API': { lookFor: 'Accessible cart endpoint', where: '{domain}/cart.js', pass: 'HTTP 200 response' },
  'Product Data API': { lookFor: 'Products JSON endpoint', where: '{domain}/products.json', pass: 'HTTP 200 + valid JSON' },
  'Payment Handler': { lookFor: 'Payment method keywords', where: 'Homepage HTML', pass: 'Shop Pay, Apple Pay, Google Pay, PayPal, Klarna detected' },
  'Checkout Flow': { lookFor: 'Checkout accessibility', where: '{domain}/checkout', pass: 'HTTP 200 or 401' },
  'Inventory Signals': { lookFor: 'Stock level indicators', where: 'Homepage HTML', pass: '"in stock", "out of stock", "only X left" detected' },
  // Product Data Architecture (Industry-Agnostic)
  'Product Identifiers': { lookFor: 'SKU, GTIN, EAN, UPC, ISBN, MPN', where: 'Product JSON-LD', pass: 'At least 1 universal identifier' },
  'Commerce Data': { lookFor: 'Price, availability in Offer schema', where: 'Product JSON-LD offers', pass: 'Price + availability structured' },
  'Brand & Categorization': { lookFor: 'Brand, category, productType', where: 'Product JSON-LD', pass: 'Brand or category present' },
  'Product-Specific Attributes': { lookFor: 'additionalProperty entries', where: 'Product JSON-LD', pass: '2+ custom attributes structured' },
  'Attribute Depth': { lookFor: 'Overall schema richness', where: 'Product JSON-LD analysis', pass: '60%+ depth score' },
  'Structured vs Free Text': { lookFor: 'Schema properties vs prose', where: 'Product JSON-LD', pass: '10+ properties (not just description)' },
  'Product Schema': { lookFor: 'JSON-LD with @type="Product"', where: 'Sample product page (not homepage)', pass: 'Valid Product schema in JSON-LD' },
  'Product Markup': { lookFor: 'JSON-LD with @type="Product"', where: 'Sample product page', pass: 'Valid Product schema detected' },
  'Organization Schema': { lookFor: 'JSON-LD with Organization type', where: 'Homepage', pass: '@type="Organization" or "LocalBusiness"' },
  'Breadcrumb': { lookFor: 'BreadcrumbList JSON-LD', where: 'Homepage/Product pages', pass: '@type="BreadcrumbList" present' },
  'Open Graph': { lookFor: 'OG meta tags', where: 'Homepage HTML', pass: '<meta property="og:*"> tags present' },
  'Shipping': { lookFor: 'Shipping page/policy', where: '/pages/shipping, /policies/shipping-policy', pass: 'HTTP 200' },
  'Returns': { lookFor: 'Returns/refund page', where: '/pages/returns, /policies/refund-policy', pass: 'HTTP 200' },
  'Privacy': { lookFor: 'Privacy policy page', where: '/pages/privacy, /policies/privacy-policy', pass: 'HTTP 200' },
  'FAQ': { lookFor: 'Help/FAQ content', where: '/pages/faq, /pages/faqs, /help', pass: 'HTTP 200' },
  'Contact': { lookFor: 'Contact information', where: '/pages/contact, /contact-us', pass: 'HTTP 200' },
  'Guest Checkout': { lookFor: 'Guest checkout option', where: 'Checkout/Homepage', pass: '"guest checkout", "continue as guest" detected' },
  'Account Creation': { lookFor: 'Account system', where: 'Homepage HTML', pass: '"create account", "sign up" detected' },
  'Saved Payment': { lookFor: 'Payment storage', where: 'Homepage/Checkout', pass: '"save card", "saved payment" detected' },
  'OAuth': { lookFor: 'Social login options', where: 'Homepage HTML', pass: 'Google, Facebook, Apple sign-in detected' },
  'Collection Pages': { lookFor: 'Collections endpoint', where: '{domain}/collections', pass: 'HTTP 200' },
  'Collection Schema': { lookFor: 'CollectionPage JSON-LD', where: 'Collections page', pass: '@type="CollectionPage"' },
  'default': { lookFor: 'Various indicators', where: 'Homepage and key pages', pass: 'Relevant content detected' }
};

const getMethodology = (checkName) => {
  for (const [key, value] of Object.entries(CHECK_METHODOLOGY)) {
    if (checkName.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }
  return CHECK_METHODOLOGY['default'];
};

const SharedReportPage = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showShareToast, setShowShareToast] = useState(false);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await axios.get(`${API_URL}/api/analyses/${analysisId}`);
        
        setResults({
          ...response.data,
          storeName: response.data.domain,
        });
      } catch (err) {
        console.error('Failed to load analysis:', err);
        if (err.response?.status === 404) {
          setError('This report could not be found. It may have been removed or the link is invalid.');
        } else {
          setError('Unable to load report. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (analysisId) {
      fetchAnalysis();
    }
  }, [analysisId]);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `AI Readiness Report - ${results?.domain}`,
          text: `Check out this AI readiness analysis for ${results?.domain}`,
          url: shareUrl
        });
        return;
      } catch (err) {
        // Fall through to clipboard
      }
    }
    
    await navigator.clipboard.writeText(shareUrl);
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 3000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <Check className="w-4 h-4 text-emerald-400" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4 text-amber-400" />;
      case 'failed':
        return <X className="w-4 h-4 text-orange-400" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'passed':
        return <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">Passed</span>;
      case 'partial':
        return <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">Needs Work</span>;
      case 'failed':
        return <span className="text-xs text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded">Failed</span>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Loader2 className="w-16 h-16 text-[#f5f5f0]/40 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-light mb-2">Loading report</h2>
          <p className="text-[#f5f5f0]/50">Fetching analysis data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-6" />
          <h2 className="text-2xl font-light mb-2">Report Not Found</h2>
          <p className="text-[#f5f5f0]/50 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline"
              className="border-[#f5f5f0]/20 text-[#f5f5f0]"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Analyse a Store
            </Button>
            <Button 
              className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0]"
              onClick={() => window.open(HUBSPOT_LINK, '_blank')}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Get Expert Help
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!results) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f0]">
      {/* Share Toast Notification */}
      {showShareToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] bg-emerald-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">Link copied to clipboard!</span>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <PacerLogo width={100} height={28} />
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <Button 
              variant="outline" 
              size="sm"
              className="border-[#f5f5f0]/20 text-[#f5f5f0] hover:bg-[#f5f5f0]/10"
              onClick={handleShare}
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Share</span>
            </Button>
            <Button 
              size="sm"
              className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0]"
              onClick={() => window.open(HUBSPOT_LINK, '_blank')}
              data-testid="schedule-call-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              <span className="hidden md:inline">Schedule Call</span>
              <span className="md:hidden">Book</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 md:pt-24 pb-32 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-[#f5f5f0]/50 hover:text-[#f5f5f0] transition-colors mb-6 md:mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Analyse another store
          </button>

          {/* Score Header with Donut */}
          <div className="bg-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5 mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <DonutChart 
                value={results.overallScore} 
                size={160}
                strokeWidth={12}
                sublabel="Score"
              />

              <div className="flex-1 text-center md:text-left w-full">
                <h1 className="text-2xl md:text-3xl font-light mb-2">{results.storeName}</h1>
                <p className="text-[#f5f5f0]/50 mb-4 md:mb-6">AI Readiness Report</p>
                
                <ScoreSummaryBar 
                  passed={results.passed}
                  partial={results.partial}
                  failed={results.failed}
                />
              </div>
            </div>
          </div>

          {/* Pillar Scores - Discovery / Transaction / Infrastructure */}
          {results.pillars && results.pillars.length > 0 && (
            <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8" data-testid="pillar-scores">
              {results.pillars.map((pillar) => {
                const colorMap = {
                  discovery: { bg: 'border-blue-500/20', label: 'text-blue-400' },
                  transaction: { bg: 'border-emerald-500/20', label: 'text-emerald-400' },
                  infrastructure: { bg: 'border-violet-500/20', label: 'text-violet-400' },
                };
                const colors = colorMap[pillar.id] || colorMap.infrastructure;
                
                return (
                  <div
                    key={pillar.id}
                    className={`bg-[#111111] rounded-xl md:rounded-2xl p-4 md:p-6 border ${colors.bg}`}
                    data-testid={`pillar-${pillar.id}`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <SmallDonut value={pillar.score} size={64} strokeWidth={5} />
                      <h3 className={`text-sm md:text-base font-medium mt-3 ${colors.label}`}>{pillar.name}</h3>
                      <p className="text-[10px] md:text-xs text-[#f5f5f0]/40 mt-1 leading-tight">{pillar.description}</p>
                      <span className="text-[10px] text-[#f5f5f0]/30 mt-2">{pillar.weight}% of overall</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Overview */}
          <div className="bg-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5 mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-medium mb-3 md:mb-4">Overview</h2>
            <p className="text-sm md:text-base text-[#f5f5f0]/60 leading-relaxed" dangerouslySetInnerHTML={{ __html: results.overview?.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#f5f5f0]">$1</strong>') }} />
            
            {/* Detected Apps Badge */}
            {results.detectedApps && results.detectedApps.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-[#f5f5f0]/40 uppercase tracking-wider">Detected Apps:</span>
                  {results.detectedApps.map((app, i) => (
                    <span 
                      key={i}
                      className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                    >
                      {app}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Top Priorities */}
          {results.topPriorities && results.topPriorities.length > 0 && (
            <div className="bg-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5 mb-6 md:mb-8">
              <h2 className="text-lg md:text-xl font-medium mb-4 md:mb-6">Top Priorities</h2>
              <div className="space-y-3 md:space-y-4">
                {results.topPriorities.map((priority, i) => (
                  <div key={i} className="flex items-start gap-3 md:gap-4 p-3 md:p-4 bg-[#0a0a0a] rounded-xl">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-[#f5f5f0] rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-[#0a0a0a] font-bold text-xs md:text-sm">{priority.id}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm md:text-base mb-1">{priority.title}</h4>
                      <p className="text-xs md:text-sm text-[#f5f5f0]/50">{priority.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shopify Advantage Card - Only for non-Shopify stores */}
          {!results.isShopify && results.potentialShopifyScore && (
            <div className="bg-gradient-to-br from-emerald-500/10 to-[#111111] rounded-xl md:rounded-2xl p-5 md:p-8 border border-emerald-500/20 mb-6 md:mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                <h2 className="text-lg md:text-xl font-medium">The Shopify Advantage</h2>
              </div>
              <p className="text-sm text-[#f5f5f0]/50 mb-6">
                Your platform isn't leveraging Shopify's built-in commerce primitives. Here's how your score could improve:
              </p>
              <div className="flex items-center justify-around gap-4">
                <div className="text-center">
                  <SmallDonut value={results.overallScore} size={80} strokeWidth={6} />
                  <p className="text-xs text-[#f5f5f0]/40 mt-2">Current Score</p>
                </div>
                <div className="flex flex-col items-center">
                  <ArrowRight className="w-8 h-8 text-emerald-400" />
                  <span className="text-emerald-400 font-bold text-lg mt-1">
                    +{results.potentialShopifyScore - results.overallScore} points
                  </span>
                </div>
                <div className="text-center">
                  <SmallDonut value={results.potentialShopifyScore} size={80} strokeWidth={6} />
                  <p className="text-xs text-[#f5f5f0]/40 mt-2">With Shopify</p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-white/5 text-center">
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => window.open(MIGRATION_LINK, '_blank')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Explore Shopify Migration
                </Button>
              </div>
            </div>
          )}

          {/* What Shopify Gives You Section - Enhanced Visual Breakdown for non-Shopify stores */}
          {!results.isShopify && results.shopifyWouldHandle && results.shopifyWouldHandle.length > 0 && (
            <div className="bg-gradient-to-br from-emerald-500/5 to-[#0d0d0d] rounded-xl md:rounded-2xl p-5 md:p-8 border border-emerald-500/20 mb-6 md:mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-medium">Shopify Would Auto-Fix {results.shopifyWouldHandle.length} Checks</h2>
                    <p className="text-xs text-[#f5f5f0]/40">Features included out-of-the-box on Shopify</p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 text-xs text-[#f5f5f0]/50">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Critical</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"></span> Important</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#f5f5f0]/30"></span> Standard</span>
                </div>
              </div>
              
              {/* Group checks by category */}
              {(() => {
                const grouped = results.shopifyWouldHandle.reduce((acc, item) => {
                  if (!acc[item.category]) acc[item.category] = [];
                  acc[item.category].push(item);
                  return acc;
                }, {});
                
                return (
                  <div className="space-y-4">
                    {Object.entries(grouped).map(([category, checks]) => (
                      <div key={category} className="bg-[#0a0a0a]/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-emerald-400">{category}</h3>
                          <span className="text-xs text-[#f5f5f0]/40">{checks.length} check{checks.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {checks.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm group">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                item.currentStatus === 'failed' 
                                  ? 'bg-orange-400/20' 
                                  : 'bg-amber-400/20'
                              }`}>
                                <ArrowRight className={`w-3 h-3 ${
                                  item.currentStatus === 'failed' 
                                    ? 'text-orange-400' 
                                    : 'text-amber-400'
                                }`} />
                              </div>
                              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                              <span className="text-[#f5f5f0]/70 flex-1 truncate">{item.check}</span>
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                item.weight === 3 ? 'bg-red-400' :
                                item.weight === 2 ? 'bg-amber-400' :
                                'bg-[#f5f5f0]/30'
                              }`} title={item.weight === 3 ? 'Critical' : item.weight === 2 ? 'Important' : 'Standard'}></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              
              {/* Summary footer */}
              <div className="mt-4 pt-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-sm text-[#f5f5f0]/60">
                    <span className="text-emerald-400 font-medium">{results.shopifyWouldHandle.filter(c => c.weight === 3).length} critical</span> and{' '}
                    <span className="text-amber-400 font-medium">{results.shopifyWouldHandle.filter(c => c.weight === 2).length} important</span> checks would pass automatically
                  </p>
                </div>
                <Button 
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => window.open(MIGRATION_LINK, '_blank')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Explore Migration
                </Button>
              </div>
            </div>
          )}

          {/* Implementation Roadmap Section */}
          {results.categories && (
            <div className="bg-gradient-to-br from-[#111111] to-[#0d0d0d] rounded-xl md:rounded-2xl p-5 md:p-8 border border-white/5 mb-6 md:mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-medium">Your Implementation Roadmap</h2>
                  <p className="text-xs text-[#f5f5f0]/40">Prioritized actions to improve your AI readiness score</p>
                </div>
              </div>

              {(() => {
                const actionItems = [];
                results.categories.forEach(category => {
                  category.checks?.forEach(check => {
                    if (check.status !== 'passed') {
                      actionItems.push({ ...check, categoryName: category.name });
                    }
                  });
                });
                actionItems.sort((a, b) => {
                  if (b.weight !== a.weight) return (b.weight || 1) - (a.weight || 1);
                  if (a.status === 'failed' && b.status === 'partial') return -1;
                  if (a.status === 'partial' && b.status === 'failed') return 1;
                  return 0;
                });

                const critical = actionItems.filter(i => i.weight === 3);
                const important = actionItems.filter(i => i.weight === 2);
                const standard = actionItems.filter(i => i.weight === 1 || !i.weight);

                return (
                  <div className="space-y-6">
                    {critical.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Flag className="w-4 h-4 text-red-400" />
                          <h3 className="text-sm font-medium text-red-400">Phase 1: Critical Priority</h3>
                          <span className="text-xs text-[#f5f5f0]/30">({critical.length} items)</span>
                        </div>
                        <div className="space-y-2 pl-6 border-l-2 border-red-500/30">
                          {critical.slice(0, 5).map((item, i) => (
                            <div key={i} className="bg-[#0a0a0a]/50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full ${item.status === 'failed' ? 'bg-orange-400' : 'bg-amber-400'}`}></span>
                                <span className="text-sm font-medium">{item.name}</span>
                              </div>
                              <p className="text-xs text-[#f5f5f0]/50 mb-2">{item.recommendation}</p>
                              <span className="text-[10px] text-[#f5f5f0]/30">{item.categoryName}</span>
                            </div>
                          ))}
                          {critical.length > 5 && <p className="text-xs text-[#f5f5f0]/30 pl-3">+ {critical.length - 5} more</p>}
                        </div>
                      </div>
                    )}
                    {important.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Flag className="w-4 h-4 text-amber-400" />
                          <h3 className="text-sm font-medium text-amber-400">Phase 2: Important</h3>
                          <span className="text-xs text-[#f5f5f0]/30">({important.length} items)</span>
                        </div>
                        <div className="space-y-2 pl-6 border-l-2 border-amber-500/30">
                          {important.slice(0, 4).map((item, i) => (
                            <div key={i} className="bg-[#0a0a0a]/50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full ${item.status === 'failed' ? 'bg-orange-400' : 'bg-amber-400'}`}></span>
                                <span className="text-sm font-medium">{item.name}</span>
                              </div>
                              <p className="text-xs text-[#f5f5f0]/50">{item.recommendation}</p>
                            </div>
                          ))}
                          {important.length > 4 && <p className="text-xs text-[#f5f5f0]/30 pl-3">+ {important.length - 4} more</p>}
                        </div>
                      </div>
                    )}
                    {standard.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Flag className="w-4 h-4 text-[#f5f5f0]/40" />
                          <h3 className="text-sm font-medium text-[#f5f5f0]/60">Phase 3: Nice to Have</h3>
                          <span className="text-xs text-[#f5f5f0]/30">({standard.length} items)</span>
                        </div>
                        <div className="space-y-2 pl-6 border-l-2 border-[#f5f5f0]/10">
                          {standard.slice(0, 3).map((item, i) => (
                            <div key={i} className="bg-[#0a0a0a]/50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`w-2 h-2 rounded-full ${item.status === 'failed' ? 'bg-orange-400' : 'bg-amber-400'}`}></span>
                                <span className="text-sm font-medium">{item.name}</span>
                              </div>
                              <p className="text-xs text-[#f5f5f0]/50">{item.recommendation}</p>
                            </div>
                          ))}
                          {standard.length > 3 && <p className="text-xs text-[#f5f5f0]/30 pl-3">+ {standard.length - 3} more</p>}
                        </div>
                      </div>
                    )}
                    <div className="mt-6 pt-4 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <p className="text-sm text-[#f5f5f0]/50">
                        <span className="text-red-400 font-medium">{critical.length}</span> critical, 
                        <span className="text-amber-400 font-medium"> {important.length}</span> important, 
                        <span className="text-[#f5f5f0]/60"> {standard.length}</span> standard items
                      </p>
                      <Button size="sm" className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0]" onClick={() => window.open(HUBSPOT_LINK, '_blank')}>
                        <Calendar className="w-4 h-4 mr-2" />
                        Get Implementation Help
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Score Breakdown */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-medium mb-4 md:mb-6">Score Breakdown</h2>
            <p className="text-sm md:text-base text-[#f5f5f0]/50 mb-6 md:mb-8">How your store performs across each category</p>

            {/* Category Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
              {results.categories.map((category) => (
                <div 
                  key={category.id}
                  className="bg-[#111111] rounded-xl p-4 md:p-5 border border-white/5"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <SmallDonut value={category.score} size={40} strokeWidth={3} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-xs md:text-sm truncate">{category.name}</h3>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:gap-3 mt-2 md:mt-3 text-[10px] md:text-xs text-[#f5f5f0]/40">
                    <span className="text-emerald-400">{category.passed}✓</span>
                    <span className="text-amber-400">{category.partial}~</span>
                    <span className="text-orange-400">{category.failed}✗</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Detailed Breakdown */}
            <div className="space-y-3 md:space-y-4">
              {results.categories.map((category) => (
                <Accordion type="single" collapsible key={category.id}>
                  <AccordionItem 
                    value={category.id}
                    className="bg-[#111111] rounded-xl border border-white/5 overflow-hidden"
                  >
                    <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 hover:no-underline hover:bg-white/5">
                      <div className="flex items-center gap-3 md:gap-4 text-left w-full">
                        <SmallDonut value={category.score} size={36} strokeWidth={3} />
                        <div className="flex-1">
                          <h3 className="font-medium text-sm md:text-base">{category.name}</h3>
                          <p className="text-[10px] md:text-xs text-[#f5f5f0]/40">{category.description}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                      <div className="space-y-3 md:space-y-4 pt-3 md:pt-4 border-t border-white/5">
                        {category.checks.map((check, j) => (
                          <div 
                            key={j}
                            className="p-3 md:p-4 bg-[#0a0a0a] rounded-lg"
                          >
                            {/* Check Header */}
                            <div className="flex items-start gap-2 md:gap-3 mb-3">
                              <div className="mt-0.5">
                                {getStatusIcon(check.status)}
                              </div>
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className="font-medium text-xs md:text-sm">{check.name}</span>
                                  {/* Methodology Tooltip */}
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button className="text-[#f5f5f0]/30 hover:text-[#f5f5f0]/60 transition-colors">
                                          <HelpCircle className="w-3.5 h-3.5" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-xs bg-[#1a1a1a] border-white/10 text-[#f5f5f0] p-3">
                                        <div className="space-y-2 text-xs">
                                          <div>
                                            <span className="text-[#f5f5f0]/50">Looking for:</span>
                                            <p className="text-[#f5f5f0]/90">{getMethodology(check.name).lookFor}</p>
                                          </div>
                                          <div>
                                            <span className="text-[#f5f5f0]/50">Where:</span>
                                            <p className="text-[#f5f5f0]/90">{getMethodology(check.name).where}</p>
                                          </div>
                                          <div>
                                            <span className="text-[#f5f5f0]/50">Pass criteria:</span>
                                            <p className="text-emerald-400">{getMethodology(check.name).pass}</p>
                                          </div>
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  {getStatusLabel(check.status)}
                                  {/* Weight/Importance Badge */}
                                  {check.weight && (
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                      check.weight === 3 ? 'bg-red-500/20 text-red-400' :
                                      check.weight === 2 ? 'bg-amber-500/20 text-amber-400' :
                                      'bg-[#f5f5f0]/10 text-[#f5f5f0]/40'
                                    }`}>
                                      {check.weight === 3 ? 'Critical' : check.weight === 2 ? 'Important' : 'Standard'}
                                    </span>
                                  )}
                                  {/* Shopify Automatic Badge */}
                                  {check.shopifyAutomatic && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                                      <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M15.5 2.5L14.3 5.5L11 4L12 7.5L8.5 8.5L11.5 10.5L9 14L12.5 12.5L14 16L15.5 12.5L19 14L16.5 10.5L19.5 8.5L16 7.5L17 4L13.7 5.5L15.5 2.5Z"/>
                                      </svg>
                                      Shopify Auto
                                    </span>
                                  )}
                                  {/* Shopify App Badge */}
                                  {check.shopifyApp && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 flex items-center gap-1">
                                      <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                                        <path d="M9 12l2 2 4-4"/>
                                      </svg>
                                      {check.shopifyApp}
                                    </span>
                                  )}
                                  {/* Shopify Would Handle Ghost Badge - for non-Shopify stores */}
                                  {!results.isShopify && check.shopifyWouldHandle && check.status !== 'passed' && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400/60 border border-emerald-500/20 border-dashed flex items-center gap-1">
                                      <Sparkles className="w-2.5 h-2.5" />
                                      Shopify would fix
                                    </span>
                                  )}
                                </div>
                                <p className="text-[10px] md:text-xs text-[#f5f5f0]/50">{check.description}</p>
                              </div>
                            </div>
                            
                            {/* Observation & Recommendation */}
                            {(check.observation || check.recommendation) && (
                              <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
                                {check.observation && (
                                  <div className="flex items-start gap-2">
                                    <Eye className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <span className="text-[10px] text-blue-400 font-medium uppercase tracking-wider">Observation</span>
                                      <p className="text-[11px] md:text-xs text-[#f5f5f0]/60 mt-1">{check.observation}</p>
                                    </div>
                                  </div>
                                )}
                                {check.recommendation && (
                                  <div className="flex items-start gap-2">
                                    <Lightbulb className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <span className="text-[10px] text-amber-400 font-medium uppercase tracking-wider">Recommendation</span>
                                      <p className="text-[11px] md:text-xs text-[#f5f5f0]/60 mt-1">{check.recommendation}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          {!results.isShopify && results.potentialShopifyScore ? (
            <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-xl md:rounded-2xl p-5 md:p-8 border border-emerald-500/20 text-center mb-8">
              <h3 className="text-lg md:text-xl font-medium mb-2">Ready to boost your score?</h3>
              <p className="text-sm md:text-base text-[#f5f5f0]/50 mb-4 md:mb-6 max-w-md mx-auto">
                Migration could boost your score to {results.potentialShopifyScore}. 
                Schedule a free consultation to discuss your Shopify migration path.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  variant="outline"
                  className="border-[#f5f5f0]/20 text-[#f5f5f0] hover:bg-[#f5f5f0]/10"
                  onClick={handleShare}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Report
                </Button>
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 md:h-12 px-6 md:px-8"
                  onClick={() => window.open(MIGRATION_LINK, '_blank')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Explore Shopify Migration
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 rounded-xl md:rounded-2xl p-5 md:p-8 border border-emerald-500/20 text-center mb-8">
              <h3 className="text-lg md:text-xl font-medium mb-2">Want to improve this score?</h3>
              <p className="text-sm md:text-base text-[#f5f5f0]/50 mb-4 md:mb-6 max-w-md mx-auto">
                Schedule a free consultation with our experts to get personalised recommendations 
                and a roadmap to full AI readiness.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  variant="outline"
                  className="border-[#f5f5f0]/20 text-[#f5f5f0] hover:bg-[#f5f5f0]/10"
                  onClick={handleShare}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Report
                </Button>
                <Button 
                  className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0] h-11 md:h-12 px-6 md:px-8"
                  onClick={() => window.open(HUBSPOT_LINK, '_blank')}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Free Consultation
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-md border-t border-white/5 py-3 md:py-4 px-4 md:px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <SmallDonut value={results.overallScore} size={36} strokeWidth={3} />
            <div>
              <p className="text-xs md:text-sm font-medium">AI Readiness Score</p>
              <p className="text-[10px] md:text-xs text-[#f5f5f0]/40">{results.failed} issues to address</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              className="border-[#f5f5f0]/20 text-[#f5f5f0] hover:bg-[#f5f5f0]/10 h-9 md:h-10 px-3 md:px-4 text-xs md:text-sm"
              onClick={handleShare}
            >
              <Share2 className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden md:inline ml-2">Share</span>
            </Button>
            <Button 
              className="bg-[#f5f5f0] text-[#0a0a0a] hover:bg-[#e5e5e0] h-9 md:h-10 px-4 md:px-6 text-xs md:text-sm"
              onClick={() => window.open(HUBSPOT_LINK, '_blank')}
            >
              <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
              <span className="hidden md:inline">Schedule Call</span>
              <span className="md:hidden">Book</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedReportPage;
